import type {
    MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode as SearchTrendsData,
    MarketResearchDemandAnalysisKeywordListsModelsIdentifiersKeywordGroupIdentifier as KeywordGroupIdentifier,
    MarketResearchDemandAnalysisKeywordListsModelsIdentifiersSearchTermIdentifier as SearchTermIdentifier,
    MarketResearchDemandAnalysisKeywordListsModelsIdentifiersAiTopicIdentifier as AiTopicIdentifier,
} from "@similarweb/pro-api-client";

import {
    buildKeywordGroupKeywordListId,
    buildSearchTermKeywordListId,
    buildAiTopicKeywordListId,
} from "./id-builders";
import type { KeywordListEntity } from "./types";

type Node = NonNullable<SearchTrendsData["children"]>[number];
type Identifier = KeywordGroupIdentifier | SearchTermIdentifier | AiTopicIdentifier;

function isKeywordGroupIdentifier(identifier: Identifier): identifier is KeywordGroupIdentifier {
    return identifier.type === "KeywordGroup";
}

function isSearchTermIdentifier(identifier: Identifier): identifier is SearchTermIdentifier {
    return identifier.type === "SearchTerm";
}

function isAiTopicIdentifier(identifier: Identifier): identifier is AiTopicIdentifier {
    return identifier.type === "AiTopic";
}

export function createKeywordGroupEntity(identifier: KeywordGroupIdentifier, name: string) {
    return {
        name,
        id: identifier.keywordGroupId,
        type: "keywordGroup",
        keywordListId: buildKeywordGroupKeywordListId(identifier.keywordGroupId),
    } satisfies KeywordListEntity;
}

export function createSearchTermEntity(identifier: SearchTermIdentifier, name: string) {
    return {
        name,
        id: identifier.seed,
        type: "searchTerm",
        keywordListId: buildSearchTermKeywordListId(identifier.seed, identifier.country),
    } satisfies KeywordListEntity;
}

export function createAiTopicEntity(identifier: AiTopicIdentifier, name: string) {
    return {
        name,
        id: identifier.seed,
        type: "aiTopic",
        keywordListId: buildAiTopicKeywordListId(identifier.seed),
    } satisfies KeywordListEntity;
}

export function createKeywordListEntity(node: Node): KeywordListEntity | undefined {
    if (!("identifier" in node)) {
        return undefined;
    }

    if (isKeywordGroupIdentifier(node.identifier)) {
        return createKeywordGroupEntity(node.identifier, node.name);
    }

    if (isSearchTermIdentifier(node.identifier)) {
        return createSearchTermEntity(node.identifier, node.name);
    }

    if (isAiTopicIdentifier(node.identifier)) {
        return createAiTopicEntity(node.identifier, node.name);
    }

    return undefined;
}
